import React, { useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import type { LearnLegal, LearnLegalStatus } from '@/client/types/learn';
import { useLearnLegalReviewMutation } from '@/client/api/learn';

const STATUS_LABEL: Record<LearnLegalStatus, string> = {
  not_sent: 'Not reviewed',
  in_review: 'In review',
  changes_requested: 'Changes requested',
  cleared: 'Cleared',
};

interface Props {
  slug: string;
  legal: LearnLegal;
}

/**
 * Shown when the section's text matched policy words (refund, warranty...).
 * Publishing stays blocked until a reviewer marks it Cleared. The API records
 * the signed-in admin as reviewer and refuses callers without
 * `content.legal.review`.
 */
export const LegalReviewPanel: React.FC<Props> = ({ slug, legal }) => {
  const review = useLearnLegalReviewMutation();
  const [notes, setNotes] = useState(legal.notes ?? '');
  const cleared = legal.status === 'cleared';

  const decide = async (status: LearnLegalStatus) => {
    try {
      await review.mutateAsync({ slug, status, notes: notes.trim() || undefined });
      toast.success(`Legal review: ${STATUS_LABEL[status]}`);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to record legal review');
    }
  };

  return (
    <div
      className={`rounded-xl border p-5 shadow-sm space-y-3 ${
        cleared ? 'border-emerald-200 bg-emerald-50/40' : 'border-amber-200 bg-amber-50/50'
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {cleared ? (
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          ) : (
            <ShieldAlert className="h-4 w-4 text-amber-600" />
          )}
          <span className="text-sm font-semibold text-gray-900">Legal review</span>
          <Badge variant="secondary" className="text-[11px]">
            {STATUS_LABEL[legal.status] ?? legal.status}
          </Badge>
        </div>
        {legal.reviewer && (
          <span className="text-[11px] text-gray-500">
            Last decision by {legal.reviewer}
            {legal.reviewed_at ? ` on ${new Date(legal.reviewed_at).toLocaleString()}` : ''}
          </span>
        )}
      </div>

      <p className="text-xs text-gray-700">
        {cleared
          ? 'This text has been cleared and can be published. Editing the text sends it back for review.'
          : 'This section uses words that can read as a policy promise, so it cannot be published until it is cleared.'}
      </p>

      {legal.triggers && legal.triggers.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] text-gray-500">Flagged words:</span>
          {legal.triggers.map((t) => (
            <code key={t} className="rounded bg-white px-1.5 py-0.5 text-[11px] text-gray-800 border border-gray-200">
              {t}
            </code>
          ))}
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="learn-legal-notes" className="text-xs">
          Reviewer notes
        </Label>
        <textarea
          id="learn-legal-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder="e.g. Owner decision: wording is general advice, not a refund policy."
          className="w-full rounded-md border border-gray-300 bg-white p-2 text-xs text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          onClick={() => decide('cleared')}
          disabled={review.isPending || cleared}
          className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          {review.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Clear for publishing
        </Button>
        <Button size="sm" variant="outline" onClick={() => decide('changes_requested')} disabled={review.isPending}>
          Request changes
        </Button>
        <Button size="sm" variant="outline" onClick={() => decide('in_review')} disabled={review.isPending}>
          Mark in review
        </Button>
      </div>
    </div>
  );
};
